// Shared fetch hook for widgets: collapses the cancelled-flag /
// loading / error boilerplate into `useDashboardFetch(url, deps)`.
// Pass a null URL to disable + reset; deps defaults to [url].

import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { fetchingAPI } from '@/api'

// In-flight GETs shared by URL: co-mounted widgets that derive the
// same request (e.g. two comparative widgets both reading by_criteria
// with identical filters) piggyback on one network call instead of
// hitting the ClickHouse-backed endpoint twice per page load. Entries
// are removed as soon as the request settles -- this is request
// coalescing, not a response cache.
const inFlightByUrl = new Map()

const coalescedGet = (url, dispatch) => {
  const existing = inFlightByUrl.get(url)
  if (existing) {
    return existing
  }
  const promise = fetchingAPI(url, 'GET', dispatch).finally(() => {
    inFlightByUrl.delete(url)
  })
  inFlightByUrl.set(url, promise)
  return promise
}

export const useDashboardFetch = (url, deps) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Default to depending on the URL string -- works for any caller
  // that builds the URL from filters, since the string changes when
  // any filter dimension changes. Explicit `deps` is for callers that
  // need to refire on something the URL doesn't capture.
  const effectDeps = deps ?? [url]

  useEffect(() => {
    if (!url) {
      setData(null)
      setLoading(false)
      setError(null)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    coalescedGet(url, dispatch)
      .then((result) => {
        if (!cancelled) {
          setData(result)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null)
          setError(err?.message || 'Failed to load')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, effectDeps)

  return { data, loading, error }
}
